import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AdminPageHeader, Select } from "./_helpers";
import { toast } from "sonner";
import { Trash2, Upload, Loader2, ShieldCheck, Wrench } from "lucide-react";
import { mediaUrl } from "@/lib/api";

const PAGE_SIZE = 60;

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [provinces, setProvinces] = useState([]);
  const [scope, setScope] = useState("shared");
  const [provinceId, setProvinceId] = useState("");
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async (append = false) => {
    setLoading(true);
    try {
      const skip = append ? items.length : 0;
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(skip),
      });
      if (search.trim()) params.set("search", search.trim());
      const r = await api.get(`/media?${params}`);
      const next = r.data.items || [];
      setItems((prev) => (append ? [...prev, ...next] : next));
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Dosyalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
    api.get("/provinces?active_only=false").then((r) => setProvinces(r.data));
    // eslint-disable-next-line
  }, []);

  // Debounced reload on search change
  useEffect(() => {
    const t = setTimeout(() => load(false), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const upload = async () => {
    if (!file) return toast.error("Dosya seçin");
    if (scope === "local" && !provinceId) return toast.error("Lokal için il seçin");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("scope", scope);
    if (provinceId) fd.append("province_id", provinceId);
    if (title) fd.append("title", title);
    try {
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null);
      setTitle("");
      load(false);
      toast.success("Yüklendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const removeOne = async (id) => {
    try {
      await api.delete(`/media/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  // ─── Bulk selection ─────────────────────────────────────────────
  const toggleOne = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const visibleIds = items.map((m) => m.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someSelected = visibleIds.some((id) => selectedIds.includes(id)) && !allSelected;
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };
  const clearSelection = () => setSelectedIds([]);
  const bulkDelete = async () => {
    if (selectedIds.length === 0) return toast.error("Önce dosya seçin");
    if (!window.confirm(`${selectedIds.length} dosyayı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/media/bulk-delete`, { ids: selectedIds });
      toast.success(`${r.data.deleted} dosya silindi`);
      setItems((prev) => prev.filter((m) => !selectedIds.includes(m.id)));
      setTotal((t) => Math.max(0, t - r.data.deleted));
      setSelectedIds([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu silme başarısız");
    } finally {
      setBulkBusy(false);
    }
  };

  const [integrity, setIntegrity] = useState(null);
  const [checking, setChecking] = useState(false);
  const runIntegrity = async (repair = false) => {
    setChecking(true);
    try {
      const r = await api.get(`/media/integrity?limit=1000${repair ? "&repair=true" : ""}`);
      setIntegrity(r.data);
      if (repair && r.data.repaired_count > 0) {
        toast.success(`${r.data.repaired_count} kayıt onarıldı`);
      } else if (r.data.missing_count === 0) {
        toast.success(`${r.data.checked} dosyanın hepsi depoda mevcut`);
      } else {
        toast.message(`${r.data.missing_count} / ${r.data.checked} dosya depoda bulunamadı`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kontrol başarısız");
    } finally {
      setChecking(false);
    }
  };

  const hasMore = items.length < total;

  return (
    <div data-testid="admin-media-page">
      <AdminPageHeader
        title="Dosya Havuzu"
        subtitle={`Ortak ve il bazlı medya • Toplam ${total.toLocaleString("tr-TR")} dosya (bu sayfada ${items.length})`}
      />
      <div className="bg-white border border-gray-100 rounded-lg p-4 mb-4 grid md:grid-cols-6 gap-2 items-end">
        <Select
          label="Havuz"
          value={scope}
          options={[{ value: "shared", label: "Ortak" }, { value: "local", label: "İl Lokal" }]}
          onChange={(e) => setScope(e.target.value)}
        />
        <Select
          label="İl"
          value={provinceId}
          options={[{ value: "", label: "-" }, ...provinces.map((p) => ({ value: p.id, label: p.name }))]}
          onChange={(e) => setProvinceId(e.target.value)}
        />
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-600">Başlık</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-600">Dosya</label>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0])} className="text-sm" />
        </div>
        <button
          onClick={upload}
          data-testid="admin-media-upload"
          className="px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold flex items-center gap-1 h-fit"
        >
          <Upload className="w-4 h-4" /> Yükle
        </button>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-600">Ara</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Dosya adı veya başlık..."
            className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
            data-testid="admin-media-search"
          />
        </div>
      </div>

      {/* Integrity check row — guards against lost photos after redeploy */}
      <div className="bg-white border border-gray-100 rounded-lg p-3 mb-4 flex items-center gap-3 flex-wrap text-sm">
        <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
        <div className="flex-1 min-w-[200px] text-gray-700">
          Dosya havuzu kalıcı depoda saklanır. Yine de kayıp olup olmadığını kontrol edebilirsiniz.
          {integrity && (
            <span className={`ml-2 text-xs ${integrity.missing_count > 0 ? "text-red-600" : "text-emerald-600"}`}>
              • {integrity.checked} kontrol edildi, {integrity.missing_count} eksik{integrity.repaired_count > 0 ? `, ${integrity.repaired_count} onarıldı` : ""}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => runIntegrity(false)}
          disabled={checking}
          data-testid="admin-media-integrity-check"
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 rounded text-xs font-semibold disabled:opacity-50"
        >
          {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
          Bütünlük Kontrolü
        </button>
        {integrity && integrity.missing_count > 0 && (
          <button
            type="button"
            onClick={() => runIntegrity(true)}
            disabled={checking}
            data-testid="admin-media-integrity-repair"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-semibold disabled:opacity-50"
          >
            <Wrench className="w-3.5 h-3.5" /> Otomatik Onar
          </button>
        )}
      </div>

      {integrity && integrity.missing && integrity.missing.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-xs text-red-800" data-testid="admin-media-missing-list">
          <div className="font-bold mb-1">⚠ Depoda bulunamayan {integrity.missing_count} dosya (ilk 50):</div>
          <ul className="list-disc list-inside space-y-0.5 max-h-40 overflow-y-auto">
            {integrity.missing.map((m) => (
              <li key={m.id}>
                <span className="font-mono">{m.stored_name}</span> — {m.title || "(başlıksız)"}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bulk actions bar */}
      {selectedIds.length > 0 && (
        <div
          className="mb-3 bg-red-50 border border-red-200 rounded p-3 text-sm flex items-center justify-between gap-3 flex-wrap"
          data-testid="admin-media-bulk-bar"
        >
          <div className="text-red-900">
            <strong>{selectedIds.length}</strong> dosya seçildi
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="admin-media-bulk-delete"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#e11d2a] text-white rounded hover:bg-[#c01825] disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Seçilileri Sil
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-gray-200 text-gray-700 rounded hover:bg-gray-50"
              data-testid="admin-media-bulk-clear"
            >
              Seçimi Temizle
            </button>
          </div>
        </div>
      )}

      {/* Grid header with "select all" */}
      {items.length > 0 && (
        <div className="flex items-center gap-2 mb-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[#e11d2a] cursor-pointer"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = someSelected; }}
              onChange={toggleAll}
              data-testid="admin-media-select-all"
            />
            Bu sayfadaki tüm dosyaları seç
          </label>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-media-grid">
        {items.map((m) => {
          const selected = selectedIds.includes(m.id);
          return (
          <div
            key={m.id}
            className={`relative bg-white border rounded-lg overflow-hidden transition-shadow ${
              selected ? "border-[#e11d2a] ring-2 ring-[#e11d2a]/30 shadow-md" : "border-gray-100"
            }`}
          >
            {/* Checkbox overlay */}
            <label className="absolute top-1.5 left-1.5 z-10 inline-flex items-center justify-center w-6 h-6 bg-white/90 rounded border border-gray-300 cursor-pointer hover:bg-white">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#e11d2a] cursor-pointer"
                checked={selected}
                onChange={() => toggleOne(m.id)}
                data-testid={`admin-media-select-${m.id}`}
              />
            </label>
            {m.mime_type?.startsWith("image") ? (
              <img src={mediaUrl(m.url)} alt={m.title} className="w-full aspect-square object-cover" />
            ) : (
              <div className="aspect-square bg-gray-100 flex items-center justify-center text-xs">{m.mime_type}</div>
            )}
            <div className="p-2">
              <div className="text-xs font-medium truncate">{m.title}</div>
              <div className="text-[10px] text-gray-400">{m.scope}</div>
              <div className="flex justify-between items-center mt-1">
                <button
                  onClick={() => navigator.clipboard.writeText(mediaUrl(m.url))}
                  className="text-xs text-blue-600"
                >
                  URL Kopyala
                </button>
                <button
                  onClick={() => removeOne(m.id)}
                  className="text-red-500"
                  data-testid={`admin-media-delete-${m.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center mt-5">
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            data-testid="admin-media-load-more"
            className="inline-flex items-center gap-2 px-5 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-md text-sm font-semibold disabled:opacity-50"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Daha Fazla Yükle ({items.length} / {total})
          </button>
        </div>
      )}
      {items.length === 0 && !loading && (
        <div className="text-center text-gray-500 py-10 bg-white border border-gray-100 rounded-lg">
          Henüz dosya yok.
        </div>
      )}
    </div>
  );
}
